import jwtAxios from 'main/services/auth/jwt-auth';
import {notEmpty} from 'main/utility/CommonUtil';
import {
  triggerCSVDownload,
  triggerPDFDownload,
} from 'main/utility/helper/FileUtil';
// import invoiceData from 'pages/staticPages/Invoice1/invoiceData';
import {
  WIDGET_FETCH_ERROR,
  WIDGET_FETCH_START,
  WIDGET_FETCH_SUCCESS,
  WIDGET_FIELD_OPTIONS,
} from 'shared/constants/ActionTypes';
import {
  isSuccessResponse,
  showError,
  showMessage,
  toFormDataParam,
} from './CommonController';

/**
 * Global Utility to handle API lifecycles
 * Handles: Loading Start -> API Call -> Success/Error Dispatch -> Loading End
 */
export const handleWidgetApiCall = async (
  widgetId,
  dispatch,
  apiCall,
  successAction,
) => {
  dispatch(fetchWidgetStart(widgetId));
  try {
    const response = await apiCall();

    const message = response.data?.message;
    if (!isSuccessResponse(response)) {
      throw new Error(message);
    }

    const dataBody = response.data?.body;
    dispatch(fetchWidgetSuccess(widgetId, dataBody));
    notEmpty(message) && dispatch(showMessage(message));

    if (typeof successAction === 'function') {
      successAction(dataBody, widgetId);
    } else if (successAction) {
      dispatch({type: successAction, payload: dataBody, widgetId});
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      'Error: Something went wrong.';
    handleWidgetError(widgetId, dispatch, errorMsg);
  }
};

const handleWidgetError = (widgetId, dispatch, error) => {
  dispatch({
    type: WIDGET_FETCH_ERROR,
    widgetId,
  });
  notEmpty(error) && dispatch(showError(error));
};

const fetchWidgetStart = (widgetId) => ({
  type: WIDGET_FETCH_START,
  widgetId,
});

export const fetchWidgetSuccess = (widgetId, payload) => ({
  type: WIDGET_FETCH_SUCCESS,
  payload,
  widgetId,
});

// STATIC SAMPLE DATA
export const getWidgetStaticData = (widgetId) => (dispatch) => {
  // console.log(
  //   `[getWidgetStaticData] widgetId: ${widgetId}, requestPayload: ${JSON.stringify(
  //     requestPayload,
  //   )}`,
  // );
  let dataBody;
  const invoiceData = {};
  switch (widgetId) {
    case 'w-lLhOV1JaPk9OEKPwbjFDz': // Invoice
      dataBody = invoiceData;
      break;
    default:
      dataBody = [];
  }
  dispatch(fetchWidgetSuccess(widgetId, dataBody));
};

// FORM DATA
export const getWidgetData = (widgetId, requestPayload) => (dispatch) => {
  handleWidgetApiCall(widgetId, dispatch, () =>
    jwtAxios.post(`/w/data/${widgetId}/get`, requestPayload),
  );
};

export const saveWidgetData = (widgetId, data) => (dispatch) => {
  handleWidgetApiCall(
    widgetId,
    dispatch,
    () => jwtAxios.post(`/w/data/${widgetId}/save`, data),
    () => {
      dispatch(showMessage('Changes saved successfully.'));
    },
  );
};

export const saveWidgetDataWithFile =
  (widgetId, requestPayload) => (dispatch) => {
    const payload = toFormDataParam(requestPayload);
    handleWidgetApiCall(
      widgetId,
      dispatch,
      // Pass the config object as the third argument to axios.post
      () =>
        jwtAxios.post(`/w/data/fileData/${widgetId}/save`, payload, {
          headers: {'Content-Type': 'multipart/form-data'},
        }),
      () => {
        dispatch(showMessage('Changes saved successfully.'));
      },
    );
  };

export const getWidgetFieldOptions =
  (widgetId, fieldName, requestPayload) => async (dispatch) => {
    try {
      const response = await jwtAxios.post(
        `/w/data/${widgetId}/options/${fieldName}`,
        requestPayload,
      );

      const message = response.data?.message;
      if (!isSuccessResponse(response)) {
        throw new Error(message);
      }

      const dataBody = response.data?.body;
      dispatch({
        type: WIDGET_FIELD_OPTIONS,
        widgetId: widgetId,
        payload: dataBody,
      });
      notEmpty(message) && dispatch(showMessage(message));
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error.message ||
        `Error loading field options.`;
      handleWidgetError(widgetId, dispatch, errorMsg);
    }
  };

// TABLE DATA
export const getWidgetDataList = (widgetId, requestPayload) => (dispatch) => {
  handleWidgetApiCall(widgetId, dispatch, () =>
    jwtAxios.post(`/w/data/${widgetId}/getList`, requestPayload),
  );
};

export const modifyBatchWidgetData =
  (widgetId, requestPayload) => (dispatch) => {
    return handleWidgetApiCall(
      widgetId,
      dispatch,
      () => jwtAxios.post(`/w/data/${widgetId}/batch`, requestPayload),
      () => {
        dispatch(showMessage('Batch action completed successfully'));
      },
    );
  };

export const getWidgetExportData =
  (widgetId, requestPayload, dataColumns) => async (dispatch) => {
    // IMP: Since its the same API call used to get the Table data, therefore not using
    // handleWidgetApiCall() to avoid collision with Table widget data and state.

    dispatch(showMessage('Exporting data...'));

    try {
      const response = await jwtAxios.post(
        `/w/data/${widgetId}/getList`,
        requestPayload,
      );

      const message = response.data?.message;
      if (!isSuccessResponse(response)) {
        throw new Error(message);
      }

      const rows = response.data?.body?.rows || [];
      if (rows.length === 0) {
        dispatch(showError('No data available to export.'));
        return;
      }

      const format = requestPayload.reportType?.toLowerCase() || 'csv';
      const headers = dataColumns
        .filter((column) => column.show)
        .map((column) => ({
          label: column.label || column.name,
          key: column.name,
        }));

      if (format === 'pdf') {
        triggerPDFDownload(rows, headers, `ExportedData.pdf`);
      } else {
        triggerCSVDownload(rows, headers, `ExportedData.csv`);
      }
      dispatch(showMessage('Data exported successfully.'));
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error.message ||
        'Error exporting the data, please try again later.';
      handleWidgetError(widgetId, dispatch, errorMsg);
    }
  };
